// Mentis Offline Storage Utility
// Manages local caching for offline access to flashcards, quizzes, and pending sync actions

const KEYS = {
  FLASHCARDS: 'mentis_offline_flashcards_',
  FLASHCARD_STATS: 'mentis_offline_flashcard_stats_',
  PENDING_REVIEWS: 'mentis_pending_reviews_',
  SAVED_QUIZZES: 'mentis_saved_quizzes_',
  LAST_QUIZ: 'mentis_last_quiz',
};

const safeGet = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export const offlineStorage = {
  // ── Flashcards ──────────────────────────────────────────────────
  saveFlashcards: (userId, cards) => {
    if (!userId || !Array.isArray(cards)) return false;
    return safeSet(KEYS.FLASHCARDS + userId, {
      cards,
      savedAt: Date.now(),
    });
  },

  loadFlashcards: (userId) => {
    if (!userId) return [];
    const data = safeGet(KEYS.FLASHCARDS + userId);
    return data?.cards ?? [];
  },

  saveFlashcardStats: (userId, stats) => {
    if (!userId || !stats) return false;
    return safeSet(KEYS.FLASHCARD_STATS + userId, stats);
  },

  loadFlashcardStats: (userId) => {
    if (!userId) return null;
    return safeGet(KEYS.FLASHCARD_STATS + userId);
  },

  // ── Pending Reviews (offline queue) ─────────────────────────────
  addPendingReview: (userId, cardId, difficulty) => {
    if (!userId) return false;
    const existing = safeGet(KEYS.PENDING_REVIEWS + userId) ?? [];
    const updated = [...existing, { cardId, difficulty, queuedAt: Date.now() }];
    return safeSet(KEYS.PENDING_REVIEWS + userId, updated);
  },

  getPendingReviews: (userId) => {
    if (!userId) return [];
    return safeGet(KEYS.PENDING_REVIEWS + userId) ?? [];
  },

  clearPendingReviews: (userId) => {
    if (!userId) return;
    localStorage.removeItem(KEYS.PENDING_REVIEWS + userId);
  },

  // ── Quizzes ──────────────────────────────────────────────────────
  saveQuiz: (userId, quiz, label) => {
    if (!userId || !quiz) return false;
    const existing = safeGet(KEYS.SAVED_QUIZZES + userId) ?? [];
    const newEntry = {
      id: Date.now(),
      label: label || `Quiz ${new Date().toLocaleDateString('de-DE')}`,
      quiz,
      savedAt: Date.now(),
    };
    // Keep max 5 saved quizzes
    const updated = [newEntry, ...existing].slice(0, 5);
    return safeSet(KEYS.SAVED_QUIZZES + userId, updated);
  },

  loadSavedQuizzes: (userId) => {
    if (!userId) return [];
    return safeGet(KEYS.SAVED_QUIZZES + userId) ?? [];
  },

  deleteSavedQuiz: (userId, quizId) => {
    if (!userId) return;
    const existing = safeGet(KEYS.SAVED_QUIZZES + userId) ?? [];
    safeSet(KEYS.SAVED_QUIZZES + userId, existing.filter(q => q.id !== quizId));
  },

  // Last generated quiz (auto-save, no user action needed)
  saveLastQuiz: (quiz) => safeSet(KEYS.LAST_QUIZ, { quiz, savedAt: Date.now() }),
  loadLastQuiz: () => {
    const data = safeGet(KEYS.LAST_QUIZ);
    return data?.quiz ?? null;
  },
};

export default offlineStorage;
